import NiceModal, { useModal } from '@ebay/nice-modal-react';
import userflow from 'userflow.js';

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { Video } from 'components/global/Video';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { ONBOARDING } from 'services/consts';

import { updateInstructionData } from 'stores/InstructionSlice';

import AppLogger from 'utils/AppLogger';

import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OnboardingVideo.styled';

const OnboardingVideo = NiceModal.create(({ rewardData }) => {
	const modal = useModal();
	const [page, setPage] = useState(0);
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { showError } = useAlert();
	const { LanguageCode } = useLocale();
	const videoUrl = useMemo(() => {
		if (LanguageCode === 'arabicValue') {
			return rewardData.videoUrl.ar;
		} else if (LanguageCode === 'engValue') {
			return rewardData.videoUrl.en;
		} else {
			return rewardData.videoUrl.tr;
		}
	}, [rewardData, LanguageCode]);

	const fullSkip = () => {
		axiosPrivate
			.post(ONBOARDING.UPDATE_ONBOARDING, {
				monetizedIntroVideo: true
			})
			.then((response) => {
				if (response.status === 200) {
					dispatch(updateInstructionData({ monetizedIntroVideo: true }));
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
			});
	};

	useEffect(() => {
		userflow.closeResourceCenter();

		const listener = () => {
			if (document.visibilityState === 'visible') {
				if (window.YT.Player && window.monetizeVideo) {
					window.monetizeVideo.playVideo();
				}
			} else {
				if (window.YT.Player && window.monetizeVideo) {
					window.monetizeVideo.pauseVideo();
				}
			}
		};
		document.addEventListener('visibilitychange', listener);

		return () => document.removeEventListener('visibilitychange', listener);
	}, []);

	if (page === 0)
		return (
			<VideoPage
				onClick={() => {
					axiosPrivate
						.post(ONBOARDING.COMPLETE_VIDEO_REWARD)
						.then(() => {
							setPage(2);
							fetchRemainingAmount();
							dispatch(updateInstructionData({ monetizedIntroVideo: true }));
						})
						.catch((error) => {
							showError({
								message: error?.response?.data?.code || error?.response?.data?.message
							});
							AppLogger.error(error);
						});
				}}
				onCancel={() => setPage(1)}
				videoUrl={videoUrl}
			/>
		);
	else if (page === 1)
		return (
			<OnboardingBluePage
				title="videoExitTitle"
				description="videoExitDescription"
				buttonText="videoExitButton"
				image="https://storage.googleapis.com/tryoto-public/onboarding/welcomeVideo.svg"
				reward={rewardData.reward}
				currency={rewardData.currency}
				nextHandler={() => setPage(0)}
				skipButton={() => {
					fullSkip();
					modal.remove();
				}}
			/>
		);
	else if (page === 2)
		return (
			<OnboardingBluePage
				title="videoDoneTitle"
				description="videoDoneDescription"
				buttonText="videoDoneButton"
				image="https://storage.googleapis.com/tryoto-public/onboarding/completedVideo.svg"
				reward={rewardData.reward}
				currency={rewardData.currency}
				nextHandler={() => modal.remove()}
				skipButton={() => modal.remove()}
				showSkip={false}
			/>
		);
});

const VideoPage = ({ onClick, onCancel, videoUrl }) => (
	<Modal fullwidth onClose={() => onCancel() || true}>
		<S.OnboardingVideoPageContainer>
			<Typography variant="h2" fontWeight="700">
				activationSubject
			</Typography>
			<Typography variant="p1" fontWeight="500" noWrap>
				videoDescription
			</Typography>
			<Video
				variant="inline"
				overlayImage="https://storage.googleapis.com/tryoto-public/tutorial_images/welcome-ceo-message.png"
				videoId={videoUrl.startsWith('https://youtu.be/') ? videoUrl.split('be/')[1] : videoUrl.split('v=')[1]}
				width="100%"
				height="fit-content"
				aspectRatio="16/9"
				onEnded={() => onClick()}
			/>
		</S.OnboardingVideoPageContainer>
	</Modal>
);

const OnboardingBluePage = ({
	title,
	image,
	description,
	buttonText,
	nextHandler,
	skipButton,
	reward,
	currency,
	showSkip = true
}) => {
	const { t } = useTranslation();

	return (
		<Modal fullwidth padding="0" onClose={() => skipButton() || true}>
			<S.OnboardingVideoOuterContainer>
				<Image
					url="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
					width="8rem"
					height="auto"
				/>
				<S.OnboardingVideoContainer>
					<Typography variant="h2" color="var(--white)" fontWeight="700" fontSize="5rem" noWrap>
						<Trans
							i18nKey={title}
							components={{
								black: <span style={{ color: 'var(--black)' }} />
							}}
							values={{ reward, currency: t(currency) }}
						/>
					</Typography>

					<Image url={image} width="auto" height="30vh" />
					<Typography variant="p1" fontWeight="500" color="var(--white)" fontSize="3rem" noWrap>
						<Trans
							i18nKey={description}
							components={{
								black: <span style={{ color: 'var(--black)' }} />
							}}
							values={{ reward, currency: t(currency) }}
						/>
					</Typography>
					<Button
						label={t(buttonText, { reward, currency: t(currency) })}
						variant="black"
						icon={faArrowRight}
						iconPos="right"
						size="x-large"
						onClick={nextHandler}
					/>
					{skipButton && showSkip && <Button label="skipVideo" css={S.SkipCss} onClick={skipButton} />}
				</S.OnboardingVideoContainer>
			</S.OnboardingVideoOuterContainer>
		</Modal>
	);
};

export default OnboardingVideo;
